import React, { useState } from 'react';
import { useRecoilState } from 'recoil';
import atoms from '../Atoms';
import { Button } from '@mui/material';
import Link from '@mui/material/Link';
import CircularProgress from '@mui/material/CircularProgress';
import { Link as RouterLink } from "react-router-dom";
import MuiAlert from '@mui/material/Alert';
import styled from 'styled-components';
import SSO from '../SSO';

const FlowContainer = styled.div`
  width: 100%;
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: center;
`;

const JourneyStepContainer = styled.div`
  display: flex;
  min-width: 22rem;
  max-width: 22rem;
  padding: .5rem 1rem;
  margin-bottom: 1rem;

  @media (min-aspect-ratio: 3/4) {
    border-left: #FFC840 1px solid;
    border-right: #FFC840 1px solid;
    margin-left: -1px;
  }
`;

const JourneyStepSubContainer = styled.div`
  display: flex;
  gap: .8rem;
  padding: 1rem;
  border-radius: 1.5rem;
  &:hover {
    background: #252525;
    cursor: default
  }
`;

const StepCircle = styled.div`
  border-radius: 50%;
  border: #FFC840 1px solid;
  min-width: 2.7rem;
  min-height: 2.7rem;
  max-width: 2.7rem;
  max-height: 2.7rem;
  font-size: 1.8rem;
  margin: 0;
  padding: 0;
  text-align: center;
`;

const JourneyBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-right: 1rem;
  justify-content: space-between;
`;

const JourneyTitle = styled.div`
  font-size: 1.2rem;
`;

const JourneyButton = styled(Button)`
  display: flex;
  align-self: flex-start;
  :hover {
    background: #008EFF;
    text-decoration: none;
  }
`;

const SuccessAlert = styled(MuiAlert)`
  align-self: flex-start;
  padding: 0.4rem 0.7rem;
  font-size: 14px;
  display: flex;
  justify-content: center;
  width: 100%;
  border-width: 2px;
  cursor: default;
  div { padding: 0; line-height: 1.3rem }
`;

const Header = styled.div`
  border-top: #626671 1px solid;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 1rem 0;
  padding: 1rem 0;
  .img { width: 3rem; height: 3rem }
  .title { font-size: 1.3rem; font-weight: 700 }
  .subtitle { font-size: 1rem; }
`;

export default () => {
  const [ jwt ] = useRecoilState(atoms.jwt);
  const { launchSso } = SSO();

  const JourneyStep = ({ index, title, body, button, href, to, ssoSite }) => {
    const [ loading, setLoading ] = useState(false);

    let buttonProps = {};
    if (href) {
      buttonProps = { href, component: Link, target: '_blank', rel: 'noopener' };
    } else if (to) {
      buttonProps = { to, component: RouterLink, };
    } else if (ssoSite) {
      buttonProps = {
        disabled: loading,
        onClick: () => launchSso(ssoSite, setLoading),
        endIcon: loading ? <CircularProgress size={10} /> : '',
      };
    }

    return (
      <JourneyStepContainer>
        <JourneyStepSubContainer>
          <StepCircle>{index + 1}</StepCircle>
          <JourneyBody>
            <JourneyTitle>{title}</JourneyTitle>
            {body}
            {
              (to === '/register' && jwt) ? (
                <SuccessAlert variant="outlined" severity="success">
                  DONE!
                </SuccessAlert>
              ) : (
                <Button variant="blue" {...buttonProps}>
                  {button}
                </Button>
              )
            }
          </JourneyBody>
        </JourneyStepSubContainer>
      </JourneyStepContainer>
    );
  };

  const entries = [
    {
      title: 'Become a part of the Community',
      body: 'Get fast answers to your questions and connect with other developers.',
      button: 'Sign Up',
      to: '/register',
    },
    {
      title: 'Research top solutions',
      body: 'Learn about all the ways to streamline work using integrated bots and apps.',
      button: 'Documentation',
      href: '//docs.developers.symphony.com'
    },
    {
      title: 'Become a Certified Symphony Developer',
      body: 'A great way to validate your Symphony skills and boost your career as the demand for Symphony experts continues to grow!',
      button: 'Training Center',
      ssoSite: 'training-center',
    },
    {
      title: 'Get advice from the experts',
      body: 'Technical forums deliver peer recommendations you need when you need them.',
      button: 'Forum',
      ssoSite: 'forum',
    },
    {
      title: 'Get external recognition and credibility',
      body: 'Build scalable, innovative solutions to bring progress and efficiency to their firms. Learn new skills, meet other developer teams and win prizes.',
      button: 'Hackathons',
      to: '/events',
    },
  ];

  return (
    <>
      <Header>
        <img src="starticon.svg" className="img" alt="Start" />
        <div className="title">Start your developer Journey</div>
        <div className="subtitle">Complete your developer journey in 5 easy steps</div>
      </Header>

      <FlowContainer>
        { entries.map((entry, index) => <JourneyStep key={entry.title} index={index} {...entry} />) }
      </FlowContainer>
    </>
  );
};
